// AI Agent effectiveness scorecard — rows = bots, columns capture their
// behaviour across the window. Supervisor glance → "which bots are
// earning their keep, which need tuning?".
//
// Classification source: lib/sessionClassification.js (behavioural, not
// hardcoded). Rolls up the 6-way session classification per bot and
// infers each bot's role from its dominant pattern — router bots get
// credited for routing-success, task bots for completing-with-tools,
// transfer-focused bots for handing off cleanly.

import React, { useMemo, useState } from 'react';
import { Badge, Group, Progress, Text, Tooltip } from '@mantine/core';
import { ReportTable } from './ReportTable';
import { fmtDuration } from '../utils';
import { SessionListModal } from '../components/SessionListModal';
import {
  CLASSIFICATIONS,
  CLASSIFICATION_META,
  ROLE_META,
  ROLES,
  MIN_SESSIONS_FOR_ROLE,
  classifySessions,
  rollupByBot,
} from '../lib/sessionClassification';

const REPORT_ID = 'bot-effectiveness';

const COLUMNS = [
  {
    id: 'name',
    label: 'Bot',
    pinned: true,
    render: (r) => (
      <div>
        <Group gap={6} align="baseline">
          <Text size="sm" fw={500}>{r.name || <em>(unnamed)</em>}</Text>
          <Text size="xs" c="dimmed">
            (n={r.sessions}{r.resolvedTotal !== r.sessions ? `, resolved=${r.resolvedTotal}` : ''})
          </Text>
        </Group>
        {r.types.length > 0 && (
          <Text size="xs" c="dimmed">{r.types.join(' / ')}</Text>
        )}
      </div>
    ),
  },
  {
    id: 'role',
    label: 'Role',
    pinned: true,
    hint: `Inferred from behaviour across the window — not a hardcoded config. Needs ≥${MIN_SESSIONS_FOR_ROLE} resolved sessions; otherwise Unknown. ≥70% in one classification picks a pure role; otherwise Hybrid.`,
    render: (r) => <RoleBadge role={r.role} confidence={r.roleConfidence} resolvedTotal={r.resolvedTotal} />,
  },
  {
    id: 'sessions',
    label: 'Sessions',
    align: 'right',
    render: (r) => <Text size="sm">{r.sessions}</Text>,
  },
  {
    id: 'uniqueCallers',
    label: 'Unique callers',
    hint: 'Distinct ANIs the bot talked to in the window.',
    align: 'right',
    render: (r) => <Text size="sm">{r.uniqueCallers}</Text>,
  },
  {
    id: 'successRate',
    label: 'Success',
    hint: 'Share of sessions that either routed to another AI Agent OR resolved with tool invocations. Credits routers for routing AND task bots for completing.',
    align: 'right',
    render: (r) => <RateBadge pct={r.successRate} color="teal" />,
  },
  {
    id: 'routedToAi',
    label: 'Routed → sub-bot',
    hint: 'HANDLED + a sibling AI session on the same interactionId started after. Good signal for router bots.',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.routedToAi} color="cyan" />,
  },
  {
    id: 'routedToHuman',
    label: 'Routed → human',
    hint: 'ESCALATED, OR cleanly handed off with no next AI sibling and taskDetails shows a human landing. Expected high for transfer-focused bots; a concern for task bots.',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.routedToHuman} color="blue" />,
  },
  {
    id: 'resolvedWithTools',
    label: 'Resolved w/ tools',
    hint: 'Terminal bot completed with totalActionCompleted > 0 or fulfillmentCount > 0. Classic containment.',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.resolvedWithTools} color="teal" />,
  },
  {
    id: 'resolvedNoTools',
    label: 'Resolved no tools',
    hint: 'Terminal bot ended with no actions and no fulfillment calls. Yellow flag only if this bot is classified as a Task bot — for Router / Transfer-focused / Hybrid bots, no-tools endings are normal.',
    align: 'right',
    // Role-aware color: yellow (concern) for Task bots where no-tools is a smell,
    // gray (neutral) for other roles where it's an expected pattern.
    render: (r) => <RateBadge pct={r.pcts.resolvedNoTools} color={r.role === ROLES.TASK ? 'yellow' : 'gray'} />,
  },
  {
    id: 'abandonment',
    label: 'Abandonment',
    hint: 'Caller hung up mid-conversation.',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.abandoned} color="orange" />,
  },
  {
    id: 'failRate',
    label: 'Failed',
    hint: 'Share of sessions with outcome FAILED.',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.failed} color="red" />,
  },
  {
    id: 'evalScore',
    label: 'Avg eval',
    hint: 'Mean overallEvalScore (0–1). Webex\'s own quality rubric.',
    align: 'right',
    render: (r) => <EvalBadge score={r.avgEvalScore} />,
  },
  {
    id: 'avgMessages',
    label: 'Avg msgs',
    hint: 'Mean messages per session. Very short = hangups; very long = stuck.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgMessages?.toFixed(1) ?? '—'}</Text>,
  },
  {
    id: 'avgResponseTime',
    label: 'Avg response',
    hint: 'Mean averageResponseTime across sessions. Latency ↑ = user frustration ↑.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgResponseMs != null ? `${(r.avgResponseMs / 1000).toFixed(1)}s` : '—'}</Text>,
  },
  {
    id: 'avgDuration',
    label: 'Avg duration',
    hint: 'Mean session length.',
    align: 'right',
    render: (r) => <Text size="sm">{fmtDuration(r.avgDurationMs)}</Text>,
  },
  {
    id: 'deadAir',
    label: 'Avg dead air',
    hint: 'Mean deadAirCount per session. Spikes suggest slow backing LLM or fulfillment URL.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgDeadAir?.toFixed(1) ?? '—'}</Text>,
  },
  {
    id: 'bargeIn',
    label: 'Avg barge-in',
    hint: 'Mean bargeInCount per session. Spikes suggest TTS turns are too long or caller is impatient.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgBargeIn?.toFixed(1) ?? '—'}</Text>,
  },
  {
    id: 'topIntent',
    label: 'Top intent',
    hint: 'Most common intent string in the window.',
    render: (r) => <Text size="xs">{r.topIntent || '—'}</Text>,
  },
  {
    id: 'topEntryPoint',
    label: 'Top entry',
    render: (r) => <Text size="xs">{r.topEntryPoint || '—'}</Text>,
  },
  {
    id: 'mix',
    label: 'Outcome mix',
    hint: 'Visual 6-way split: routed→AI, resolved w/ tools, resolved no tools, routed→human, abandoned, failed.',
    width: 220,
    render: (r) => (
      <Tooltip
        label={
          `${r.pcts.routedToAi}% → sub-bot · ${r.pcts.resolvedWithTools}% resolved w/ tools · ` +
          `${r.pcts.resolvedNoTools}% resolved no tools · ${r.pcts.routedToHuman}% → human · ` +
          `${r.pcts.abandoned}% abandoned · ${r.pcts.failed}% failed`
        }
      >
        <Progress.Root size="md">
          {r.pcts.routedToAi        > 0 && <Progress.Section value={r.pcts.routedToAi}        color={CLASSIFICATION_META[CLASSIFICATIONS.ROUTED_TO_AI].color} />}
          {r.pcts.resolvedWithTools > 0 && <Progress.Section value={r.pcts.resolvedWithTools} color={CLASSIFICATION_META[CLASSIFICATIONS.RESOLVED_WITH_TOOLS].color} />}
          {r.pcts.resolvedNoTools   > 0 && <Progress.Section value={r.pcts.resolvedNoTools}   color={CLASSIFICATION_META[CLASSIFICATIONS.RESOLVED_NO_TOOLS].color} />}
          {r.pcts.routedToHuman     > 0 && <Progress.Section value={r.pcts.routedToHuman}     color={CLASSIFICATION_META[CLASSIFICATIONS.ROUTED_TO_HUMAN].color} />}
          {r.pcts.abandoned         > 0 && <Progress.Section value={r.pcts.abandoned}         color={CLASSIFICATION_META[CLASSIFICATIONS.ABANDONED].color} />}
          {r.pcts.failed            > 0 && <Progress.Section value={r.pcts.failed}            color={CLASSIFICATION_META[CLASSIFICATIONS.FAILED].color} />}
        </Progress.Root>
      </Tooltip>
    ),
  },
];

const DEFAULT_VISIBLE = [
  'sessions', 'uniqueCallers', 'successRate',
  'routedToAi', 'routedToHuman', 'resolvedWithTools', 'resolvedNoTools',
  'evalScore', 'avgResponseTime', 'mix',
];

// `analysis` (optional) — pre-computed { perBot, perBotDay, classified }
// from Analytics.jsx so we don't rebuild the sibling-session map on every
// re-render. Falls back to inline compute if not provided (backward compat
// with any caller that mounts this report directly).
//
// `onViewSession` (optional) — if provided, rows become clickable and
// open a SessionListModal with the per-bot sessions behind the row.
// Each session links to Call Detail via onViewSession(interactionId, window).
export function BotEffectiveness({ data, analysis, onViewSession, window }) {
  const sessions = data?.sessions ?? [];
  const tasks = data?.tasks ?? [];
  const rows = useMemo(() => buildRows(sessions, tasks, analysis?.perBot), [sessions, tasks, analysis?.perBot]);
  const sessionsByBot = useMemo(() => groupSessionsByBot(sessions), [sessions]);

  const [drill, setDrill] = useState(null); // { bot }

  const csvRows = useMemo(() => rows.map(toCsvRow), [rows]);
  const csvFilename = `bot-effectiveness-${new Date().toISOString().slice(0, 10)}.csv`;

  return (
    <>
      <ReportTable
        title="AI Agent effectiveness scorecard"
        subtitle={`${rows.length} bot${rows.length === 1 ? '' : 's'} active in window · role inferred from behaviour (routing vs resolution); needs ≥${MIN_SESSIONS_FOR_ROLE} resolved sessions${onViewSession ? ' · click a row to drill into its sessions' : ''}`}
        reportId={REPORT_ID}
        columns={COLUMNS}
        defaultVisibleIds={DEFAULT_VISIBLE}
        rows={rows}
        emptyLabel="No AI Agent sessions recorded in this window."
        onRowClick={onViewSession ? (r) => setDrill({ bot: r.name }) : undefined}
        csvExport={{ filename: csvFilename, rows: csvRows }}
      />
      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.bot ?? ''}
        subtitle={drill ? `All ${(sessionsByBot.get(drill.bot) ?? []).length} session(s) in window for this bot` : ''}
        sessions={drill ? (sessionsByBot.get(drill.bot) ?? []) : []}
        onViewSession={onViewSession}
        window={window}
      />
    </>
  );
}

function groupSessionsByBot(sessions) {
  const m = new Map();
  for (const s of sessions ?? []) {
    const name = s?.agentName ?? '(unnamed)';
    if (!m.has(name)) m.set(name, []);
    m.get(name).push(s);
  }
  return m;
}

function toCsvRow(r) {
  return {
    bot: r.name,
    role: r.role,
    role_confidence: r.roleConfidence != null ? r.roleConfidence.toFixed(2) : '',
    sessions: r.sessions,
    resolved_sessions: r.resolvedTotal,
    unique_callers: r.uniqueCallers,
    success_pct: r.successRate,
    routed_to_ai_pct: r.pcts.routedToAi,
    routed_to_human_pct: r.pcts.routedToHuman,
    resolved_with_tools_pct: r.pcts.resolvedWithTools,
    resolved_no_tools_pct: r.pcts.resolvedNoTools,
    abandoned_pct: r.pcts.abandoned,
    failed_pct: r.pcts.failed,
    avg_eval_score: r.avgEvalScore != null ? r.avgEvalScore.toFixed(2) : '',
    avg_messages: r.avgMessages != null ? r.avgMessages.toFixed(1) : '',
    avg_response_ms: r.avgResponseMs ?? '',
    avg_duration_ms: r.avgDurationMs ?? '',
    avg_dead_air: r.avgDeadAir != null ? r.avgDeadAir.toFixed(1) : '',
    avg_barge_in: r.avgBargeIn != null ? r.avgBargeIn.toFixed(1) : '',
    top_intent: r.topIntent ?? '',
    top_entry_point: r.topEntryPoint ?? '',
  };
}

function buildRows(sessions, tasks, perBotOverride) {
  const rolled = perBotOverride ?? rollupByBot(classifySessions(sessions, tasks));

  // Merge the per-bot numeric aggregates we still want (callers, intents,
  // eval, msg counts, latencies, duration, dead air, barge-in) with the
  // classifier roll-up.
  const extras = buildExtras(sessions);
  const extrasByName = new Map(extras.map((e) => [e.name, e]));

  return rolled
    .map((r) => {
      const extra = extrasByName.get(r.name) ?? {};
      const pcts = {
        routedToAi:        pct(r.counts[CLASSIFICATIONS.ROUTED_TO_AI], r.total),
        routedToHuman:     pct(r.counts[CLASSIFICATIONS.ROUTED_TO_HUMAN], r.total),
        resolvedWithTools: pct(r.counts[CLASSIFICATIONS.RESOLVED_WITH_TOOLS], r.total),
        resolvedNoTools:   pct(r.counts[CLASSIFICATIONS.RESOLVED_NO_TOOLS], r.total),
        abandoned:         pct(r.counts[CLASSIFICATIONS.ABANDONED], r.total),
        failed:            pct(r.counts[CLASSIFICATIONS.FAILED], r.total),
      };
      return {
        key: r.key,
        name: r.name,
        role: r.role,
        roleConfidence: r.roleConfidence,
        sessions: r.total,
        resolvedTotal: r.resolvedTotal,
        successRate: r.successRate,
        pcts,
        uniqueCallers: extra.uniqueCallers ?? 0,
        types: extra.types ?? [],
        topIntent: extra.topIntent ?? null,
        topEntryPoint: extra.topEntryPoint ?? null,
        avgEvalScore: extra.avgEvalScore ?? null,
        avgMessages: extra.avgMessages ?? null,
        avgResponseMs: extra.avgResponseMs ?? null,
        avgDurationMs: extra.avgDurationMs ?? null,
        avgDeadAir: extra.avgDeadAir ?? null,
        avgBargeIn: extra.avgBargeIn ?? null,
      };
    })
    .sort((a, b) => b.sessions - a.sessions);
}

// Numeric extras keyed by bot name — same shape the previous aggregator
// produced, kept separate so the classifier module stays pure.
function buildExtras(sessions) {
  const buckets = new Map();
  for (const s of sessions) {
    const name = s?.agentName ?? null;
    if (!name) continue;
    if (!buckets.has(name)) {
      buckets.set(name, {
        name,
        callers: new Set(),
        types: new Set(),
        intents: new Map(),
        entries: new Map(),
        sumEval: 0, countEval: 0,
        sumMsgs: 0, countMsgs: 0,
        sumResp: 0, countResp: 0,
        sumDur: 0, countDur: 0,
        sumDead: 0, countDead: 0,
        sumBarge: 0, countBarge: 0,
      });
    }
    const b = buckets.get(name);
    if (s.ani) b.callers.add(s.ani);
    if (s.agentType) b.types.add(s.agentType);
    bumpMap(b.intents, s.intent);
    bumpMap(b.entries, s.entryPoint?.name);
    if (s.overallEvalScore != null)    { b.sumEval  += s.overallEvalScore;    b.countEval  += 1; }
    if (s.noOfTotalMessages != null)   { b.sumMsgs  += s.noOfTotalMessages;   b.countMsgs  += 1; }
    if (s.averageResponseTime != null) { b.sumResp  += s.averageResponseTime; b.countResp  += 1; }
    if (s.duration != null)            { b.sumDur   += s.duration;            b.countDur   += 1; }
    if (s.deadAirCount != null)        { b.sumDead  += s.deadAirCount;        b.countDead  += 1; }
    if (s.bargeInCount != null)        { b.sumBarge += s.bargeInCount;        b.countBarge += 1; }
  }
  return [...buckets.values()].map((b) => ({
    name: b.name,
    uniqueCallers: b.callers.size,
    types: [...b.types],
    topIntent: topKey(b.intents),
    topEntryPoint: topKey(b.entries),
    avgEvalScore:  safeAvgFloat(b.sumEval,  b.countEval),
    avgMessages:   safeAvgFloat(b.sumMsgs,  b.countMsgs),
    avgResponseMs: safeAvgInt(b.sumResp,    b.countResp),
    avgDurationMs: safeAvgInt(b.sumDur,     b.countDur),
    avgDeadAir:    safeAvgFloat(b.sumDead,  b.countDead),
    avgBargeIn:    safeAvgFloat(b.sumBarge, b.countBarge),
  }));
}

function bumpMap(map, key) {
  if (!key) return;
  map.set(key, (map.get(key) ?? 0) + 1);
}
function topKey(map) {
  let best = null, bestN = 0;
  for (const [k, n] of map) if (n > bestN) { best = k; bestN = n; }
  return best;
}
function safeAvgFloat(sum, count) { return count > 0 ? sum / count : null; }
function safeAvgInt(sum, count) { return count > 0 ? Math.round(sum / count) : null; }
function pct(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }

function RoleBadge({ role, confidence, resolvedTotal }) {
  const meta = ROLE_META[role] ?? ROLE_META.unknown;
  // Low confidence (<0.3) adds an asterisk + the tooltip spells out n.
  // Dotted-border variant signals "take with a grain of salt" without
  // suppressing the role entirely (helpful for Prisma supervisors who
  // need to know the number came from a small sample).
  const lowConf = confidence != null && confidence < 0.3 && role !== 'unknown';
  const tooltip = `${meta.description}${
    resolvedTotal != null ? ` Based on ${resolvedTotal} resolved session${resolvedTotal === 1 ? '' : 's'}.` : ''
  }${confidence != null ? ` Confidence: ${Math.round(confidence * 100)}%.` : ''}`;
  return (
    <Tooltip label={tooltip} multiline w={240}>
      <Badge size="sm" color={meta.color} variant={lowConf ? 'outline' : 'light'}>
        {meta.label}{lowConf ? '*' : ''}
      </Badge>
    </Tooltip>
  );
}

function RateBadge({ pct, color }) {
  if (pct == null) return <Text size="xs" c="dimmed">—</Text>;
  const variant = pct >= 20 ? 'filled' : 'light';
  return <Badge size="sm" color={color} variant={variant}>{pct}%</Badge>;
}

function EvalBadge({ score }) {
  if (score == null) return <Text size="xs" c="dimmed">—</Text>;
  const color = score < 0.5 ? 'red' : score < 0.7 ? 'orange' : score < 0.85 ? 'yellow' : 'teal';
  return <Badge size="sm" color={color} variant="light">{score.toFixed(2)}</Badge>;
}
